import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleOff, Gift, HandCoins, Settings, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FundDetailDialog } from '@/components/giving/fund-detail-dialog';
import { GivingReceiptSmsFields } from '@/components/giving/giving-receipt-sms-fields';
import { type FundDetail } from '@/components/giving/fund-detail-panel';
import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  INSIGHTS_DATE_RANGE_PRESETS,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { CurrencySelect } from '@/components/shared/currency-select';
import { DataTable, type DataTableColumn, type SortDirection } from '@/components/shared/data-table';
import { FilterBar } from '@/components/shared/filter-bar';
import {
  decodeFundTypeValue,
  encodeFundTypeValue,
  fundTypeFilterToQuery,
  FundTypeSelect,
  fundTypeLabel,
  useFundTypeFilterOptions,
} from '@/components/shared/fund-type-select';
import { FormField } from '@/components/shared/form-field';
import { FundSelect } from '@/components/shared/fund-select';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useGivingSettings } from '@/hooks/use-giving-settings';
import { api } from '@/lib/api';
import {
  formatInsightsMoneySummary,
  insightsHasMultipleCurrencies,
} from '@/lib/money-totals';
import { ApiError } from '@/lib/api-errors';
import {
  loadFundsPagePreferences,
  persistFundsPagePreferences,
  type FundsEmptyFilter,
} from '@/lib/funds-page-preferences';
import { type GivingSettings, type ReceiptSmsSettings } from '@/lib/giving-default-funds';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatMoney, formatNumber, formatPercent } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';
import { FundImportDialog } from './fund-import-dialog';

type Fund = FundDetail;

interface FundInsights {
  activeCount: number;
  inactiveCount: number;
  currency: string;
  givenAmountMinor: number;
  givenCount: number;
  byCurrency?: Array<{ currency: string; amountMinor: number; count: number }>;
  comparison: {
    change: number;
    previousAmountMinor: number;
    previousCount: number;
  };
  byFund: Array<{
    fundId: string;
    fundName: string;
    currency: string;
    amountMinor: number;
    count: number;
  }>;
  byType: Array<{
    fundType: string;
    customTypeId?: string | null;
    customTypeName?: string | null;
    fundCount: number;
    currency: string;
    amountMinor: number;
    giftCount: number;
  }>;
}

interface FundListTotals {
  count: number;
  balanceByCurrency: Array<{ currency: string; balanceMinor: number }>;
}

const STATUS_ANY = 'any';
const TYPE_ANY = 'any';
const CURRENCY_ANY = 'any';
const EMPTY_SHOW: FundsEmptyFilter = 'show';
const EMPTY_HIDE: FundsEmptyFilter = 'hide';

const defaultReceiptSms = (): ReceiptSmsSettings => ({
  enabled: true,
  body:
    'Hi {{firstName}}, thank you for your gift of {{amount}} to {{fundName}} at {{churchName}}.',
  fundOverrides: [],
});

const toSortParam = (field: string, direction: SortDirection) =>
  direction === 'desc' ? `-${field}` : field;

export const FundsPage = () => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';

  const [insightsDateRange, setInsightsDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editing, setEditing] = useState<Fund | null>(null);
  const [detailFund, setDetailFund] = useState<Fund | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [typeValue, setTypeValue] = useState('offering');
  const [defaultCurrency, setDefaultCurrency] = useState(
    () => church?.baseCurrency ?? 'GHS',
  );
  const [hideIdentity, setHideIdentity] = useState(false);
  const [acceptsPledges, setAcceptsPledges] = useState(true);
  const [availableForPrograms, setAvailableForPrograms] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [defaultTitheFundId, setDefaultTitheFundId] = useState<string | null>(null);
  const [defaultOfferingFundId, setDefaultOfferingFundId] = useState<string | null>(null);
  const [defaultContributionFundId, setDefaultContributionFundId] = useState<string | null>(null);
  const [defaultPledgeFundId, setDefaultPledgeFundId] = useState<string | null>(null);
  const [defaultProjectFundId, setDefaultProjectFundId] = useState<string | null>(null);
  const [defaultEventRegistrationFundId, setDefaultEventRegistrationFundId] = useState<
    string | null
  >(null);
  const [receiptSms, setReceiptSms] = useState<ReceiptSmsSettings>(defaultReceiptSms);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(STATUS_ANY);
  const [filterType, setFilterType] = useState(TYPE_ANY);
  const [filterCurrency, setFilterCurrency] = useState(CURRENCY_ANY);
  const [emptyFilter, setEmptyFilter] = useState<FundsEmptyFilter>(() =>
    loadFundsPagePreferences(church?.id).emptyFilter,
  );
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState('sortOrder');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const givingSettings = useGivingSettings();

  const fundCurrencies = useMemo(() => {
    const preferred = ['GHS', 'USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR'];
    const fromChurch = [
      church?.baseCurrency,
      ...(church?.supportedCurrencies ?? []),
    ].filter((code): code is string => Boolean(code));
    return [...new Set([...fromChurch, ...preferred])];
  }, [church?.baseCurrency, church?.supportedCurrencies]);

  const canManage = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_FUNDS);

  const typeFilterOptions = useFundTypeFilterOptions(TYPE_ANY, t('funds.filters.typeAny'));

  const typeFilterQuery = useMemo(
    () => fundTypeFilterToQuery(filterType, TYPE_ANY),
    [filterType],
  );

  const periodFilterActive =
    filterDateRange.preset !== 'allTime' &&
    Boolean(filterDateRange.from) &&
    Boolean(filterDateRange.to);

  useEffect(() => {
    setEmptyFilter(loadFundsPagePreferences(church?.id).emptyFilter);
  }, [church?.id]);

  const list = useQuery({
    queryKey: [
      'giving',
      'funds',
      page,
      pageSize,
      search,
      filterStatus,
      filterType,
      filterCurrency,
      emptyFilter,
      filterDateRange.from,
      filterDateRange.to,
      sortField,
      sortDirection,
    ],
    queryFn: () =>
      api.get<{ data: Fund[]; page: { page: number; pageSize: number; totalItems: number; totalPages: number }; totals?: FundListTotals }>(
        '/giving/funds',
        {
          query: {
            page,
            pageSize,
            q: search.trim() || undefined,
            status:
              filterStatus === 'active'
                ? 'active'
                : filterStatus === 'inactive'
                  ? 'inactive'
                  : undefined,
            fundType: typeFilterQuery.fundType,
            customTypeId: typeFilterQuery.customTypeId,
            currency: filterCurrency === CURRENCY_ANY ? undefined : filterCurrency,
            hideEmpty: emptyFilter === EMPTY_HIDE ? true : undefined,
            dateFrom: periodFilterActive ? filterDateRange.from ?? undefined : undefined,
            dateTo: periodFilterActive ? filterDateRange.to ?? undefined : undefined,
            sort: toSortParam(sortField, sortDirection),
          },
        },
      ),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['giving', 'funds', 'insights', insightsDateRange.from, insightsDateRange.to],
    queryFn: () =>
      api.get<FundInsights>('/giving/funds/insights', {
        query: {
          dateFrom: insightsDateRange.from ?? undefined,
          dateTo: insightsDateRange.to ?? undefined,
        },
      }),
  });

  useEffect(() => {
    if (!settingsOpen || !givingSettings.data) return;
    setDefaultTitheFundId(givingSettings.data.defaultTitheFundId);
    setDefaultOfferingFundId(givingSettings.data.defaultOfferingFundId);
    setDefaultContributionFundId(givingSettings.data.defaultContributionFundId);
    setDefaultPledgeFundId(givingSettings.data.defaultPledgeFundId);
    setDefaultProjectFundId(givingSettings.data.defaultProjectFundId);
    setDefaultEventRegistrationFundId(givingSettings.data.defaultEventRegistrationFundId);
    setReceiptSms(givingSettings.data.receiptSms ?? defaultReceiptSms());
  }, [settingsOpen, givingSettings.data]);

  const syncSettingsDraft = () => {
    if (!givingSettings.data) return;
    setDefaultTitheFundId(givingSettings.data.defaultTitheFundId);
    setDefaultOfferingFundId(givingSettings.data.defaultOfferingFundId);
    setDefaultContributionFundId(givingSettings.data.defaultContributionFundId);
    setDefaultPledgeFundId(givingSettings.data.defaultPledgeFundId);
    setDefaultProjectFundId(givingSettings.data.defaultProjectFundId);
    setDefaultEventRegistrationFundId(givingSettings.data.defaultEventRegistrationFundId);
    setReceiptSms(givingSettings.data.receiptSms ?? defaultReceiptSms());
  };

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setCode(editing.code);
      setTypeValue(encodeFundTypeValue(editing.fundType, editing.customTypeId));
      setDefaultCurrency(editing.defaultCurrency);
      setHideIdentity(!editing.recordGiverIdentity);
      setAcceptsPledges(editing.acceptsPledges);
      setAvailableForPrograms(editing.availableForPrograms);
      setIsActive(editing.isActive);
    } else {
      setName('');
      setCode('');
      setTypeValue('offering');
      setDefaultCurrency(church?.baseCurrency ?? fundCurrencies[0] ?? 'GHS');
      setHideIdentity(false);
      setAcceptsPledges(true);
      setAvailableForPrograms(false);
      setIsActive(true);
    }
  }, [open, editing, church?.baseCurrency, fundCurrencies]);

  useEffect(() => {
    if (!detailFund) return;
    const fresh = list.data?.data.find((row) => row.id === detailFund.id);
    if (fresh) setDetailFund(fresh);
  }, [list.data, detailFund?.id]);

  const save = useMutation({
    mutationFn: () => {
      const { fundType, customTypeId } = decodeFundTypeValue(typeValue);
      const typePayload = { fundType, customTypeId };
      return editing
        ? api.patch<Fund>(`/giving/funds/${editing.id}`, {
            name,
            ...typePayload,
            defaultCurrency,
            recordGiverIdentity: !hideIdentity,
            acceptsPledges,
            availableForPrograms,
            isActive,
          })
        : api.post<Fund>('/giving/funds', {
            name,
            code,
            ...typePayload,
            recordGiverIdentity: !hideIdentity,
            acceptsPledges,
            availableForPrograms,
            defaultCurrency,
          });
    },
    onSuccess: (saved) => {
      toast.success(t('funds.saved'));
      setOpen(false);
      setEditing(null);
      setDetailFund((current) => (current && current.id === saved.id ? saved : current));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', 'active'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'funds', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['programs', 'registration-funds'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveSettings = useMutation({
    mutationFn: () =>
      api.patch<GivingSettings>('/giving/settings', {
        defaultTitheFundId,
        defaultOfferingFundId,
        defaultContributionFundId,
        defaultPledgeFundId,
        defaultProjectFundId,
        defaultEventRegistrationFundId,
        receiptSms: {
          enabled: receiptSms.enabled,
          body: receiptSms.body.trim(),
          fundOverrides: receiptSms.fundOverrides
            .filter((row) => row.fundId && row.body.trim())
            .map((row) => ({ fundId: row.fundId, body: row.body.trim() })),
        },
      }),
    onSuccess: () => {
      toast.success(t('funds.settings.saved'));
      setSettingsOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'settings'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const settingsDirty =
    givingSettings.data != null &&
    (defaultTitheFundId !== givingSettings.data.defaultTitheFundId ||
      defaultOfferingFundId !== givingSettings.data.defaultOfferingFundId ||
      defaultContributionFundId !== givingSettings.data.defaultContributionFundId ||
      defaultPledgeFundId !== givingSettings.data.defaultPledgeFundId ||
      defaultProjectFundId !== givingSettings.data.defaultProjectFundId ||
      defaultEventRegistrationFundId !== givingSettings.data.defaultEventRegistrationFundId ||
      receiptSms.enabled !== (givingSettings.data.receiptSms?.enabled ?? true) ||
      receiptSms.body.trim() !== (givingSettings.data.receiptSms?.body ?? '').trim() ||
      JSON.stringify(
        receiptSms.fundOverrides
          .filter((row) => row.fundId && row.body.trim())
          .map((row) => ({ fundId: row.fundId, body: row.body.trim() })),
      ) !==
        JSON.stringify(givingSettings.data.receiptSms?.fundOverrides ?? []));

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (filterStatus !== STATUS_ANY) count += 1;
    if (filterType !== TYPE_ANY) count += 1;
    if (filterCurrency !== CURRENCY_ANY) count += 1;
    if (emptyFilter === EMPTY_HIDE) count += 1;
    if (filterDateRange.preset !== 'allTime') count += 1;
    return count;
  }, [emptyFilter, filterCurrency, filterDateRange.preset, filterStatus, filterType, search]);

  const totalsBalance = useMemo(() => {
    const totals = list.data?.totals;
    if (!totals) return undefined;
    if (filterCurrency !== CURRENCY_ANY) {
      const match = totals.balanceByCurrency.find((row) => row.currency === filterCurrency);
      return match
        ? { amount: match.balanceMinor, currency: match.currency }
        : undefined;
    }
    if (totals.balanceByCurrency.length === 1) {
      const [only] = totals.balanceByCurrency;
      return only ? { amount: only.balanceMinor, currency: only.currency } : undefined;
    }
    const baseCurrency = church?.baseCurrency ?? 'GHS';
    const baseTotal = totals.balanceByCurrency.find((row) => row.currency === baseCurrency);
    return baseTotal
      ? { amount: baseTotal.balanceMinor, currency: baseTotal.currency }
      : undefined;
  }, [church?.baseCurrency, filterCurrency, list.data?.totals]);

  const columns: DataTableColumn<Fund>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('funds.columns.name'),
        cell: (row) => row.name,
        sortable: true,
      },
      {
        id: 'code',
        header: t('funds.columns.code'),
        cell: (row) => row.code,
        sortable: true,
      },
      {
        id: 'fundType',
        header: t('funds.columns.type'),
        cell: (row) => fundTypeLabel(row, t),
        sortable: true,
      },
      {
        id: 'balanceMinor',
        header: periodFilterActive
          ? t('funds.columns.givenInPeriod')
          : t('funds.columns.balance'),
        headerAccessory: periodFilterActive ? undefined : <Tip tipId="giving.funds.balance" />,
        money: (row) => ({
          amount: periodFilterActive ? row.givenMinor : row.balanceMinor,
          currency: row.defaultCurrency,
          parenthesiseNegative: false,
          colorBySign: !periodFilterActive,
        }),
        align: 'right',
        sortable: true,
      },
      {
        id: 'defaultCurrency',
        header: t('funds.columns.currency'),
        cell: (row) => row.defaultCurrency,
        hideBelowMd: true,
        sortable: true,
      },
      {
        id: 'isActive',
        header: t('funds.columns.status'),
        cell: (row) => (
          <Badge variant={row.isActive ? 'success' : 'neutral'}>
            {row.isActive ? t('funds.statusActive') : t('funds.inactive')}
          </Badge>
        ),
        sortable: true,
      },
    ],
    [periodFilterActive, t],
  );

  const rows = list.data?.data ?? [];

  const openEdit = (fund: Fund) => {
    setEditing(fund);
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        title={t('funds.title')}
        description={t('description')}
        tipId="giving.funds.purpose"
        headerActions={
          canManage ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={t('funds.settings.open')}
                onClick={() => {
                  syncSettingsDraft();
                  setSettingsOpen(true);
                }}
              >
                <Settings className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="outline"
                leadingIcon={<Upload className="size-4" aria-hidden />}
                aria-label={t('funds.import.action')}
                onClick={() => setImportOpen(true)}
              >
                {t('funds.import.action')}
              </Button>
              <Button
                onClick={() => {
                  setEditing(null);
                  setOpen(true);
                }}
              >
                {t('funds.create')}
              </Button>
            </div>
          ) : undefined
        }
      />
      <GivingSubnav />

      <CollapsibleInsights
        summary={[
          {
            label: t('funds.insights.active'),
            value: insights.data ? formatNumber(insights.data.activeCount) : '…',
          },
          {
            label: t('funds.insights.given'),
            value: insights.data
              ? formatInsightsMoneySummary(
                  {
                    amountMinor: insights.data.givenAmountMinor,
                    currency: insights.data.currency,
                    count: insights.data.givenCount,
                    byCurrency: insights.data.byCurrency,
                  },
                  church?.baseCurrency ?? 'GHS',
                )
              : '…',
          },
          {
            label: t('funds.insights.gifts'),
            value: insights.data ? formatNumber(insights.data.givenCount) : '…',
          },
        ]}
      >
        <DateRangeFilter
          value={insightsDateRange}
          onChange={setInsightsDateRange}
          labels={{
            today: tCommon('date.today'),
            yesterday: tCommon('date.yesterday'),
            thisWeek: tCommon('date.thisWeek'),
            lastWeek: tCommon('date.lastWeek'),
            thisMonth: tCommon('date.thisMonth'),
            lastMonth: tCommon('date.lastMonth'),
            thisQuarter: tCommon('date.thisQuarter'),
            thisYear: tCommon('date.thisYear'),
            lastYear: tCommon('date.lastYear'),
            allTime: tCommon('date.allTime'),
            custom: tCommon('date.custom'),
            from: t('funds.insights.dateFrom'),
            to: t('funds.insights.dateTo'),
          }}
          presets={INSIGHTS_DATE_RANGE_PRESETS}
        />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label={t('funds.insights.active')}
            value={insights.data ? formatNumber(insights.data.activeCount) : undefined}
            icon={<CircleCheck className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          {insightsHasMultipleCurrencies(
            insights.data
              ? {
                  byCurrency: insights.data.byCurrency,
                }
              : undefined,
          ) ? (
            insights.data!.byCurrency!.map((item) => (
              <StatCard
                key={item.currency}
                label={t('funds.insights.given')}
                money={{ amount: item.amountMinor, currency: item.currency }}
                icon={<HandCoins className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
            ))
          ) : (
            <StatCard
              label={t('funds.insights.given')}
              tipId="giving.funds.insights.given"
              money={
                insights.data
                  ? { amount: insights.data.givenAmountMinor, currency: insights.data.currency }
                  : undefined
              }
              icon={<HandCoins className="size-4" aria-hidden />}
              loading={insights.isLoading}
              comparison={
                insights.data
                  ? {
                      change: insights.data.comparison.change,
                      label: t('funds.insights.vsPreviousPeriod'),
                      increaseIsGood: true,
                      formatted: (() => {
                        const change = insights.data.comparison.change;
                        const formatted = formatPercent(Math.abs(change));
                        if (change > 0) return `+${formatted}`;
                        if (change < 0) return `-${formatted}`;
                        return formatted;
                      })(),
                    }
                  : undefined
              }
            />
          )}
          <StatCard
            label={t('funds.insights.gifts')}
            value={insights.data ? formatNumber(insights.data.givenCount) : undefined}
            icon={<Gift className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('funds.insights.inactive')}
            value={insights.data ? formatNumber(insights.data.inactiveCount) : undefined}
            icon={<CircleOff className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {(insights.data?.byFund.length ?? 0) > 0 ? (
            <ChartCard title={t('funds.insights.byFund')} height={200}>
              <SimpleBarChart
                orientation="horizontal"
                data={(insights.data?.byFund ?? []).map((row) => ({
                  label:
                    (insights.data?.byFund ?? []).some(
                      (other) =>
                        other.fundId === row.fundId && other.currency !== row.currency,
                    )
                      ? `${row.fundName} (${row.currency})`
                      : row.fundName,
                  value: row.amountMinor,
                  currency: row.currency,
                }))}
                formatValue={(value, item) =>
                  formatMoney(value, item?.currency ?? church?.baseCurrency ?? 'GHS', {
                    showSymbol: true,
                  })
                }
                height={180}
              />
            </ChartCard>
          ) : null}
          {(insights.data?.byType.length ?? 0) > 0 ? (
            <ChartCard title={t('funds.insights.byType')} height={200}>
              <SimpleBarChart
                orientation="horizontal"
                data={(insights.data?.byType ?? []).map((row) => {
                  const name = fundTypeLabel(
                    { fundType: row.fundType, customTypeName: row.customTypeName },
                    t,
                  );
                  const hasOtherCurrency = (insights.data?.byType ?? []).some(
                    (other) =>
                      other.fundType === row.fundType &&
                      (other.customTypeId ?? null) === (row.customTypeId ?? null) &&
                      other.currency !== row.currency,
                  );
                  return {
                    label: hasOtherCurrency ? `${name} (${row.currency})` : name,
                    value: row.amountMinor,
                    currency: row.currency,
                    detail: t('funds.insights.typeSummary', {
                      fundCount: row.fundCount,
                      giftCount: row.giftCount,
                    }),
                  };
                })}
                formatValue={(value, item) =>
                  formatMoney(value, item?.currency ?? church?.baseCurrency ?? 'GHS', {
                    showSymbol: true,
                  })
                }
                height={180}
              />
            </ChartCard>
          ) : null}
        </div>
      </CollapsibleInsights>

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterStatus(STATUS_ANY);
          setFilterType(TYPE_ANY);
          setFilterCurrency(CURRENCY_ANY);
          setEmptyFilter(EMPTY_SHOW);
          persistFundsPagePreferences(church?.id, { emptyFilter: EMPTY_SHOW });
          setFilterDateRange({
            preset: 'allTime',
            ...resolvePreset('allTime', timezone),
          });
          setPage(1);
        }}
        labels={{
          filters: t('funds.filters.title'),
          clearAll: t('funds.filters.clearAll'),
          applied: (count) => t('funds.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('funds.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('funds.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value ?? STATUS_ANY);
                  setPage(1);
                }}
                options={[
                  { value: STATUS_ANY, label: t('funds.filters.statusAny') },
                  { value: 'active', label: t('funds.statusActive') },
                  { value: 'inactive', label: t('funds.inactive') },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={filterType}
                onChange={(value) => {
                  setFilterType(value ?? TYPE_ANY);
                  setPage(1);
                }}
                options={typeFilterOptions}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-36 min-w-[9rem]">
              <Listbox
                value={filterCurrency}
                onChange={(value) => {
                  setFilterCurrency(value ?? CURRENCY_ANY);
                  setPage(1);
                }}
                options={[
                  { value: CURRENCY_ANY, label: t('funds.filters.currencyAny') },
                  ...fundCurrencies.map((code) => ({ value: code, label: code })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={emptyFilter}
                onChange={(value) => {
                  const next = value ?? EMPTY_SHOW;
                  setEmptyFilter(next);
                  persistFundsPagePreferences(church?.id, { emptyFilter: next });
                  setPage(1);
                }}
                options={[
                  { value: EMPTY_SHOW, label: t('funds.filters.showEmpty') },
                  { value: EMPTY_HIDE, label: t('funds.filters.hideEmpty') },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <DateRangeFilter
              value={filterDateRange}
              onChange={(range) => {
                setFilterDateRange(range);
                setPage(1);
              }}
              popoverPortal={popoverPortal}
              labels={{
                today: tCommon('date.today'),
                yesterday: tCommon('date.yesterday'),
                thisWeek: tCommon('date.thisWeek'),
                lastWeek: tCommon('date.lastWeek'),
                thisMonth: tCommon('date.thisMonth'),
                lastMonth: tCommon('date.lastMonth'),
                thisQuarter: tCommon('date.thisQuarter'),
                thisYear: tCommon('date.thisYear'),
                lastYear: tCommon('date.lastYear'),
                allTime: tCommon('date.allTime'),
                custom: tCommon('date.custom'),
                from: t('funds.filters.dateFrom'),
                to: t('funds.filters.dateTo'),
              }}
              presets={LIST_FILTER_DATE_RANGE_PRESETS}
            />
          </>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={
          <p className="text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t('funds.emptyFiltered') : t('funds.empty')}
          </p>
        }
        sort={{
          field: sortField,
          direction: sortDirection,
          onChange: (field, direction) => {
            setSortField(field);
            setSortDirection(direction);
            setPage(1);
          },
        }}
        totals={
          totalsBalance
            ? {
                balanceMinor: totalsBalance,
              }
            : undefined
        }
        totalsLabel={
          periodFilterActive
            ? t('funds.totalsGiven', { count: list.data?.totals?.count ?? 0 })
            : t('funds.totals', { count: list.data?.totals?.count ?? 0 })
        }
        onRowClick={(row) => setDetailFund(row)}
        rowActions={
          canManage
            ? (row) => (
                <Button size="sm" variant="outline" onClick={() => openEdit(row)}>
                  {tCommon('action.edit')}
                </Button>
              )
            : undefined
        }
        actionsHeader={canManage ? tCommon('table.actions') : undefined}
      />

      {list.data?.page && list.data.page.totalPages > 1 ? (
        <Pagination
          page={list.data.page.page}
          pageSize={list.data.page.pageSize}
          totalItems={list.data.page.totalItems}
          totalPages={list.data.page.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <FundDetailDialog
        fund={detailFund}
        open={detailFund !== null}
        onOpenChange={(next) => {
          if (!next) setDetailFund(null);
        }}
        onEdit={openEdit}
      />

      <Dialog
        open={settingsOpen}
        onOpenChange={(next) => {
          setSettingsOpen(next);
          if (!next) syncSettingsDraft();
        }}
      >
        <DialogContent size="xl" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('funds.settings.title')}</DialogTitle>
            <DialogDescription>{t('funds.settings.description')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('funds.settings.titheFund')}>
                {({ id }) => (
                  <FundSelect
                    id={id}
                    value={defaultTitheFundId}
                    onChange={setDefaultTitheFundId}
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
              <FormField label={t('funds.settings.offeringFund')}>
                {({ id }) => (
                  <FundSelect
                    id={id}
                    value={defaultOfferingFundId}
                    onChange={setDefaultOfferingFundId}
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
              <FormField label={t('funds.settings.contributionFund')}>
                {({ id }) => (
                  <FundSelect
                    id={id}
                    value={defaultContributionFundId}
                    onChange={setDefaultContributionFundId}
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
              <FormField label={t('funds.settings.pledgeFund')}>
                {({ id }) => (
                  <FundSelect
                    id={id}
                    value={defaultPledgeFundId}
                    onChange={setDefaultPledgeFundId}
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
              <FormField label={t('funds.settings.projectFund')}>
                {({ id }) => (
                  <FundSelect
                    id={id}
                    value={defaultProjectFundId}
                    onChange={setDefaultProjectFundId}
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
              <FormField label={t('funds.settings.eventRegistrationFund')}>
                {({ id }) => (
                  <FundSelect
                    id={id}
                    value={defaultEventRegistrationFundId}
                    onChange={setDefaultEventRegistrationFundId}
                    dataSource="registration"
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
            </div>

            <div className="mt-2 border-t border-border pt-6">
              <GivingReceiptSmsFields
                value={receiptSms}
                onChange={setReceiptSms}
                disabled={!canManage || saveSettings.isPending}
                popoverPortal="dialog"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={saveSettings.isPending}
              onClick={() => {
                syncSettingsDraft();
                setSettingsOpen(false);
              }}
            >
              {tCommon('action.cancel')}
            </Button>
            <Button
              type="button"
              loading={saveSettings.isPending}
              disabled={!settingsDirty}
              onClick={() => saveSettings.mutate()}
            >
              {tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setEditing(null);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              if (!name.trim()) return;
              if (!editing && !code.trim()) return;
              save.mutate();
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{editing ? t('funds.edit') : t('funds.create')}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t('funds.name')}</Label>
                <Input value={name} onChange={(event) => setName(event.target.value)} required />
              </div>
              <FormField
                label={t('funds.code')}
                tipId={editing ? undefined : 'giving.funds.code'}
                hint={editing ? t('funds.codeLocked') : undefined}
              >
                {({ id, ...field }) => (
                  <Input
                    id={id}
                    {...field}
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    required={!editing}
                    disabled={Boolean(editing)}
                  />
                )}
              </FormField>
              <div className="flex flex-col gap-1.5">
                <Label>{t('funds.type')}</Label>
                <FundTypeSelect
                  value={typeValue}
                  onChange={setTypeValue}
                  popoverPortal="dialog"
                />
              </div>
              <FormField label={t('funds.currency')} tipId="giving.funds.currency" popoverPortal="dialog">
                {({ id }) => (
                  <CurrencySelect
                    id={id}
                    value={defaultCurrency}
                    onChange={(next) =>
                      setDefaultCurrency(next ?? church?.baseCurrency ?? 'GHS')
                    }
                    currencies={fundCurrencies}
                    popoverPortal="dialog"
                  />
                )}
              </FormField>
              <SwitchField
                id="fund-privacy"
                label={t('funds.privacy')}
                tipId="giving.funds.privacy"
                checked={hideIdentity}
                onCheckedChange={setHideIdentity}
              />
              <SwitchField
                id="fund-accepts-pledges"
                label={t('funds.acceptsPledges')}
                tipId="giving.funds.acceptsPledges"
                checked={acceptsPledges}
                onCheckedChange={setAcceptsPledges}
              />
              <SwitchField
                id="fund-available-for-programs"
                label={t('funds.availableForPrograms')}
                tipId="giving.funds.availableForPrograms"
                checked={availableForPrograms}
                onCheckedChange={setAvailableForPrograms}
              />
              {editing ? (
                <SwitchField
                  id="fund-active"
                  label={t('funds.active')}
                  tipId="giving.funds.active"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              ) : null}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button type="submit" loading={save.isPending}>
                  {t('funds.save')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <FundImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        defaultCurrency={church?.baseCurrency ?? 'GHS'}
      />
    </div>
  );
};
